import React from 'react';
import PropTypes from 'prop-types';
import { Pill } from '@one-thd/sui-atomic-components';
import { MagicMarkdown } from '../common/MagicMarkdown';
import { PILL_STYLE } from './accentStyles';

const ASK_YOUR_OWN = 'Ask your own question';

/**
 * Pill's own hover styles shift its background colour and border colour, both of which the gradient
 * border overrides, so the hover affordance is restored on the item that wraps it. `rounded-full`
 * keeps the shadow the same shape as the pill.
 */
const PILL_ITEM_CLASSES = 'sui-max-w-full sui-min-w-0 sui-rounded-full hover:sui-shadow-base';

/**
 * A question pill.
 *
 * Stencil's `Pill` at `size="large"` is the design: 44px tall, `sui-rounded-full`, and an outline
 * fill of #FFFFFF at 60%. Only the border colour differs, so `style` — which `Pill` forwards to its
 * root — swaps that for the accent gradient and leaves every other Pill token in place.
 *
 * `component="button"` makes the root a real button rather than the `div role="button"` Pill
 * defaults to. `selectable` stays off: a pill opens the assistant, it does not hold a selection.
 */
const QuestionPill = ({ label, onClick }) => (
  <Pill
    component="button"
    size="large"
    variant="outline"
    style={PILL_STYLE}
    label={label}
    onClick={onClick}
  />
);

QuestionPill.displayName = 'QuestionPill';

QuestionPill.propTypes = {
  label: PropTypes.node,
  onClick: PropTypes.func,
};

export const QuestionPills = React.memo(({ questions = [], maxQuestions, onAskQuestion }) => {
  // The cap is applied after the text filter, so a text-less question is dropped without costing a
  // slot — later questions backfill and the row still fills to `maxQuestions`. An absent
  // `maxQuestions` renders however many were passed.
  const visibleQuestions = questions
    .filter((question) => question.text)
    .slice(0, maxQuestions);

  const handleSelect = (question) => {
    if (onAskQuestion) {
      onAskQuestion({ question: question.text, position: question.position });
    }
  };

  // Opens the assistant with nothing prepopulated, so the shopper types their own question.
  const handleAskYourOwn = () => {
    if (onAskQuestion) {
      onAskQuestion({ question: ASK_YOUR_OWN, prepopulate: false });
    }
  };

  return (
    <ul
      aria-label="Suggested questions"
      // 12px between pills, in both axes: the design's questions column is 143.73px tall, which is
      // a 28px header + a 16px gap + two 44px pill rows, leaving 12px between the rows.
      className="sui-flex sui-flex-wrap sui-items-start sui-gap-3 sui-list-none sui-p-0 sui-m-0"
    >
      {visibleQuestions.map((question) => (
        <li key={question.position} className={PILL_ITEM_CLASSES}>
          <QuestionPill
            label={<MagicMarkdown variant="pill">{question.text}</MagicMarkdown>}
            onClick={() => handleSelect(question)}
          />
        </li>
      ))}
      <li className={PILL_ITEM_CLASSES}>
        <QuestionPill label={ASK_YOUR_OWN} onClick={handleAskYourOwn} />
      </li>
    </ul>
  );
});

QuestionPills.displayName = 'QuestionPills';

QuestionPills.propTypes = {
  /** How many questions the layout shows; omitted renders every question with text. */
  maxQuestions: PropTypes.number,
  /**
   * Called when a pill is clicked, with `{ question, position }`. The ask-your-own pill sends
   * `{ question, prepopulate: false }`, which opens the drawer empty instead of asking.
   */
  onAskQuestion: PropTypes.func,
  /** Questions to render. `text` is markdown; entries without it are skipped. */
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.number.isRequired,
      text: PropTypes.string,
    })
  ),
};
